// Who the screens act as (ADR-101): the part of a signed-in principal both shells can supply. The
// AllCode admin fills it from `GET /v1/me`; the proxy's Admin area from `/api/me` (TASK-584).
// Tenancy: `orgId` is the one org this session may act in — `useOrgContext` refuses a route that
// names another. Authorization stays the server's; a screen reads this only to hide controls the
// principal could not use anyway.
import { createContext, type ComponentChildren } from "preact";
import { useContext, useMemo } from "preact/hooks";
import type { components } from "../api/schema";

export type ScopeRole = components["schemas"]["PrincipalRole"];
export type ScopePermission = components["schemas"]["Permission"];

export interface ScopeMe {
  /** The stable principal id (the audit trail's `actorId`); never shown to a person. */
  readonly id: string;
  /** The org the session belongs to; null for staff, who take theirs from the route. */
  readonly orgId: string | null;
  readonly role: ScopeRole;
  readonly permissions: readonly ScopePermission[];
}

export interface Scope {
  /** Null while signed out, or when the shell has no principal to offer. */
  readonly me: ScopeMe | null;
}

const NO_SCOPE: Scope = { me: null };

/** Provided by each shell's root; outside one there is nobody signed in. */
export const ScopeContext = createContext<Scope>(NO_SCOPE);

export function ScopeProvider({ me, children }: { me: ScopeMe | null; children: ComponentChildren }) {
  // One value per principal, so consumers re-render when `me` changes and not on every parent render.
  const scope = useMemo<Scope>(() => ({ me }), [me]);
  return <ScopeContext.Provider value={scope}>{children}</ScopeContext.Provider>;
}

/** The signed-in principal the nearest `ScopeProvider` offers, or null. */
export function useScopeMe(): ScopeMe | null {
  return useContext(ScopeContext).me;
}
